/* eslint-disable jsdoc/require-jsdoc, no-alert, no-underscore-dangle */
/* Prevent mixed storefront carts. Checkout enforcement must also use a Shopify validation function. */
(() => {
  const bypassedForms = new WeakSet();
  const pendingForms = new WeakSet();
  const rootUrl = window.Shopify?.routes?.root || '/';

  function showError(form, message) {
    form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: message } }));
  }

  function isSampleLine(item) {
    const properties = item.properties || {};
    return item.is_fabric_sample === true || item.product_type === 'fabric_samples'
      || String(properties._fabric_sample || '') === 'true'
      || String(properties._fabric_sample_bundle || '') === 'true'
      || Boolean(properties['_SKU próbek']);
  }

  function sampleQuantityForLine(item) {
    const properties = item.properties || {};
    if (properties['_SKU próbek']) {
      return String(properties['_SKU próbek']).split(',').filter((sku) => sku.trim()).length;
    }
    if (String(properties._fabric_sample_bundle || '') === 'true') {
      const savedSamples = Object.keys(properties).filter((key) => !key.startsWith('_') && properties[key]);
      return Math.max(1, savedSamples.length);
    }
    return Number(item.quantity) || 0;
  }

  function inspect(cart) {
    let sampleQuantity = 0;
    let regularQuantity = 0;

    (cart?.items || []).forEach((item) => {
      if (item.product_type === 'mws_fee_generated') return;
      if (isSampleLine(item)) sampleQuantity += sampleQuantityForLine(item);
      else regularQuantity += Number(item.quantity) || 0;
    });

    return { sampleQuantity, regularQuantity };
  }

  async function getCart() {
    const response = await fetch(`${rootUrl}cart?section_id=at-fabric-sample-cart-data`, {
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Cart unavailable');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const snapshot = html.querySelector('[data-fabric-sample-cart-data]');
    if (!snapshot) throw new Error('Cart unavailable');
    return JSON.parse(snapshot.textContent);
  }

  async function validateSampleAddition(variantIds, limit = 7) {
    const cart = await getCart();
    const state = inspect(cart);
    if (state.regularQuantity > 0) return { ...state, reason: 'mixed', remaining: 0 };

    const remaining = Math.max(0, limit - state.sampleQuantity);
    const existingIds = new Set(cart.items.map((item) => String(item.variant_id || item.id)));
    const selectedIds = variantIds.map(String);
    if (new Set(selectedIds).size !== selectedIds.length || selectedIds.some((id) => existingIds.has(id))) {
      return { ...state, reason: 'duplicate', remaining };
    }
    if (selectedIds.length > remaining) return { ...state, reason: 'limit', remaining };
    return { ...state, reason: null, remaining };
  }

  function samplePickerForForm(form) {
    // A Shopify form contains input[name="id"], which shadows form.id.
    const formId = form.getAttribute?.('id');
    if (!formId) return null;
    return Array.from(document.querySelectorAll('at-fabric-picker[selection-mode="multiple"]'))
      .find((picker) => picker.getAttribute('form-id') === formId);
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form?.matches?.('form[action*="/cart/add"]')) return;
    const samplePicker = samplePickerForForm(form);
    if (samplePicker) {
      event.preventDefault();
      event.stopImmediatePropagation();
      // Resolve the current picker on every submit, including after section rerenders.
      if (typeof samplePicker.onSampleSubmit === 'function') return samplePicker.onSampleSubmit(event);
      showError(form, window.themeVariables?.strings?.sampleCartCheckError);
      return;
    }

    if (bypassedForms.has(form)) {
      bypassedForms.delete(form);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    if (pendingForms.has(form) || submitter?.getAttribute?.('aria-busy') === 'true'
      || Array.from(form.elements || []).some((element) => element.type === 'submit' && element.getAttribute('aria-busy') === 'true')) return;
    pendingForms.add(form);
    let resubmitted = false;
    submitter?.setAttribute?.('aria-busy', 'true');

    try {
      const snapshot = await getCart();
      const state = inspect(snapshot);
      if (state.sampleQuantity > 0) {
        if (state.regularQuantity > 0) {
          showError(form, window.themeVariables?.strings?.sampleCartHasSamples);
          return;
        }
        if (form.checkValidity && !form.checkValidity()) {
          form.reportValidity();
          return;
        }
        // Recheck the real cart immediately before clearing; never delete furniture
        // added since the server-rendered sample classification was fetched.
        const currentResponse = await fetch(`${rootUrl}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!currentResponse.ok) throw new Error('Cart unavailable');
        const current = await currentResponse.json();
        const sampleIds = new Set(snapshot.items.filter(isSampleLine).map((item) => String(item.variant_id || item.id)));
        if (current.items.some((item) => item.product_type !== 'mws_fee_generated' && !sampleIds.has(String(item.variant_id || item.id)))) {
          throw new Error('Cart changed');
        }
        const clearedResponse = await fetch(`${rootUrl}cart/clear.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: '{}',
        });
        if (!clearedResponse.ok || (await clearedResponse.json()).items.length !== 0) throw new Error('Cart not cleared');
      }

      submitter?.removeAttribute?.('aria-busy');
      bypassedForms.add(form);
      if (submitter) form.requestSubmit(submitter);
      else form.requestSubmit();
      resubmitted = true;
    } catch {
      showError(form, window.themeVariables?.strings?.sampleCartCheckError || 'The cart could not be checked. Please try again.');
    } finally {
      pendingForms.delete(form);
      bypassedForms.delete(form);
      if (!resubmitted) submitter?.removeAttribute?.('aria-busy');
    }
  }, true);

  window.ATFabricSampleCart = { getCart, inspect, validateSampleAddition };
})();
